import apiClient from './client'
import type { Job, CreateJobDto, JobStats } from '@/types/job'

export const jobsApi = {
  list: async (options?: {
    limit?: number;
    offset?: number;
    status?: string;
    jobType?: string;
    scriptId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{
    jobs: Job[]
    pagination: {
      total: number
      limit: number
      offset: number
      hasMore: boolean
    }
  }> => {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.status) params.append('status', options.status)
    if (options?.jobType) params.append('jobType', options.jobType)
    if (options?.scriptId) params.append('scriptId', options.scriptId)
    if (options?.search) params.append('search', options.search)
    if (options?.dateFrom) params.append('dateFrom', options.dateFrom)
    if (options?.dateTo) params.append('dateTo', options.dateTo)
    if (options?.sortBy) params.append('sortBy', options.sortBy)
    if (options?.sortOrder) params.append('sortOrder', options.sortOrder)
    
    const response = await apiClient.get(`/jobs?${params.toString()}`)
    const data = response.data
    
    // Handle both old format (array) and new format (object with jobs and pagination)
    if (Array.isArray(data)) {
      return {
        jobs: data,
        pagination: {
          total: data.length,
          limit: data.length,
          offset: 0,
          hasMore: false,
        },
      }
    }
    
    if (data?.jobs && Array.isArray(data.jobs)) {
      return {
        jobs: data.jobs,
        pagination: data.pagination || {
          total: data.jobs.length,
          limit: data.jobs.length,
          offset: 0,
          hasMore: false,
        },
      }
    }
    
    return {
      jobs: [],
      pagination: {
        total: 0,
        limit: 0,
        offset: 0,
        hasMore: false,
      },
    }
  },

  get: async (id: string): Promise<Job> => {
    const response = await apiClient.get(`/jobs/${id}`)
    return response.data
  },

  create: async (data: CreateJobDto): Promise<Job> => {
    const response = await apiClient.post('/jobs', data)
    return response.data
  },

  cancel: async (id: string): Promise<void> => {
    await apiClient.post(`/jobs/${id}/cancel`)
  },

  retry: async (id: string): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/retry`)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/jobs/${id}`)
  },

  getStats: async (): Promise<JobStats> => {
    const response = await apiClient.get('/jobs/stats')
    return response.data
  },

  listByScriptId: async (scriptId: string, options?: { jobType?: string; status?: string; limit?: number }): Promise<Job[]> => {
    const params = new URLSearchParams()
    params.append('scriptId', scriptId)
    if (options?.jobType) params.append('jobType', options.jobType)
    if (options?.status) params.append('status', options.status)
    if (options?.limit) params.append('limit', options.limit.toString())
    
    const response = await apiClient.get(`/jobs?${params.toString()}`)
    const data = response.data
    if (Array.isArray(data)) {
      return data
    }
    if (data?.jobs && Array.isArray(data.jobs)) {
      return data.jobs
    }
    return []
  },

  /**
   * Get paginated signals from a completed execute job
   * @param jobId - Job ID
   * @param page - Page number (1-indexed)
   * @param pageSize - Number of signals per page (default: 1000, max: 10000)
   * @returns Paginated signals response
   */
  getSignals: async (jobId: string, page: number = 1, pageSize: number = 1000): Promise<{
    success: boolean
    data: any[]
    page: number
    pageSize: number
    totalPages: number | null
    hasMore: boolean
    columns: string[]
  }> => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('pageSize', Math.min(pageSize, 10000).toString())
    
    const response = await apiClient.get(`/jobs/${jobId}/signals?${params.toString()}`)
    return response.data
  },
}
